import { useEffect, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePortalRuntime } from "@/lib/portal-runtime";
import {
  canReadClaimEvidence,
  claimSearchOptions,
  claimDetailOptions,
  claimEvidenceError,
  claimWorkspaceStateKey,
} from "@/lib/claim-evidence-queries";

export function LiveClaimEvidence() {
  const runtime = usePortalRuntime();
  // Remount when selected Division changes; no retained record or filter crosses tenant context.
  return <ClaimEvidence key={runtime.tenantContext.tenantId} />;
}

function ClaimEvidence() {
  const { claims, tenantContext, mode } = usePortalRuntime();
  const cache = useQueryClient();
  const stateKey = claimWorkspaceStateKey(cache, tenantContext.tenantId);
  type WorkspaceState = {
    draft: string;
    search: string;
    classification: "HISTORICAL_REAL" | "SYNTHETIC";
    after?: string;
    selected: string | null;
  };
  const remembered = cache.getQueryData<WorkspaceState>(stateKey);
  const [draft, setDraft] = useState(remembered?.draft ?? "");
  const [search, setSearch] = useState(remembered?.search ?? "");
  const [classification, setClassification] = useState<"HISTORICAL_REAL" | "SYNTHETIC">(
    remembered?.classification ?? "HISTORICAL_REAL",
  );
  const [after, setAfter] = useState<string | undefined>(remembered?.after);
  const [selected, setSelected] = useState<string | null>(remembered?.selected ?? null);
  useEffect(() => {
    // Shell remounts may retain UI state, but never durable browser storage.
    // Existing sign-out clears QueryClient, and each Division has a separate key.
    cache.setQueryData(["portal", "claim-evidence", tenantContext.tenantId, "workspace-state"], {
      draft,
      search,
      classification,
      after,
      selected,
    });
  }, [cache, tenantContext.tenantId, draft, search, classification, after, selected]);
  const list = useQuery(
    claimSearchOptions(claims, tenantContext, {
      ...(search ? { search } : {}),
      classification,
      limit: 25,
      ...(after ? { after } : {}),
    }),
  );
  const detail = useQuery(claimDetailOptions(claims, tenantContext, selected));
  const eligible = mode === "live" && claims && canReadClaimEvidence(tenantContext);
  function submit(event: FormEvent) {
    event.preventDefault();
    setSearch(draft.trim());
    setAfter(undefined);
    setSelected(null);
  }
  const item = !detail.isError && !detail.isFetching ? detail.data?.item : undefined;
  return (
    <section
      aria-labelledby="claim-evidence-title"
      className="rounded-lg border border-border bg-card p-5 space-y-4"
    >
      <h2 id="claim-evidence-title" className="font-semibold">
        Individual claim evidence
      </h2>
      <p className="text-sm text-muted-foreground">
        {tenantContext.tenantName} · Read-only canonical evidence. No financial authority, export,
        or inferred Problem Record linkage.
      </p>
      {!eligible ? (
        <p role="status">
          Claim evidence requires live access, evidence.read permission and an explicit evidence
          scope for this Division.
        </p>
      ) : (
        <>
          <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              Search claim number or failure code
              <input
                aria-label="Search claim number or failure code"
                type="search"
                maxLength={120}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                className="block rounded border border-border bg-background p-2"
              />
            </label>
            <label className="text-sm">
              Evidence classification
              <select
                aria-label="Evidence classification"
                value={classification}
                onChange={(event) => {
                  setClassification(event.target.value as typeof classification);
                  setAfter(undefined);
                  setSelected(null);
                }}
                className="block rounded border border-border bg-background p-2"
              >
                <option value="HISTORICAL_REAL">Historical source evidence</option>
                <option value="SYNTHETIC">Synthetic demonstration only</option>
              </select>
            </label>
            <button type="submit" className="rounded bg-primary px-4 py-2 text-primary-foreground">
              Search evidence
            </button>
          </form>
          <p className="text-sm">
            {classification === "SYNTHETIC"
              ? "Synthetic demonstration evidence — prohibited from production use."
              : "Historical source evidence — not current customer operations."}
          </p>
          {list.isError ? (
            <p role="alert">{claimEvidenceError(list.error)}</p>
          ) : list.isFetching ? (
            <p role="status">Loading claim evidence…</p>
          ) : list.data ? (
            <>
              <p className="text-sm">
                {list.data.items.length} rows on this page. Eligible scoped evidence only; not a
                total count or complete source coverage.
              </p>
              {list.data.items.length === 0 ? (
                <p role="status">
                  No eligible claim evidence matches these filters. This is not proof of zero
                  claims.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        {[
                          "Claim",
                          "Status",
                          "Failure code",
                          "Product",
                          "Component",
                          "Evidence",
                        ].map((label) => (
                          <th key={label} className="text-left p-2">
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {list.data.items.map((row) => (
                        <tr key={row.id} className="border-t border-border">
                          <td className="p-2">
                            <button
                              className="underline"
                              onClick={() => setSelected(row.id)}
                              aria-label={`View claim ${row.claimNumber ?? row.id}`}
                            >
                              {row.claimNumber ?? "Claim number unavailable"}
                            </button>
                          </td>
                          <td className="p-2">{row.status}</td>
                          <td className="p-2">{row.failureCode ?? "Unavailable"}</td>
                          <td className="p-2">{row.product?.name ?? "Unavailable"}</td>
                          <td className="p-2">{row.component?.name ?? "Unavailable"}</td>
                          <td className="p-2">
                            {row.classification === "SYNTHETIC"
                              ? "Synthetic · production prohibited"
                              : "Historical"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  disabled={!after}
                  onClick={() => {
                    setAfter(undefined);
                    setSelected(null);
                  }}
                  className="rounded border p-2 disabled:opacity-50"
                >
                  First evidence page
                </button>
                <button
                  disabled={!list.data.nextCursor}
                  onClick={() => {
                    setAfter(list.data?.nextCursor ?? undefined);
                    setSelected(null);
                  }}
                  className="rounded border p-2 disabled:opacity-50"
                >
                  Next evidence page
                </button>
              </div>
            </>
          ) : null}
          {selected && (
            <section
              aria-label="Claim evidence detail"
              className="rounded border border-border p-4 space-y-2"
            >
              <h3 className="font-semibold">Claim evidence detail</h3>
              <button onClick={() => setSelected(null)} className="underline">
                Close claim detail
              </button>
              {detail.isError ? (
                <p role="alert">{claimEvidenceError(detail.error)}</p>
              ) : detail.isFetching ? (
                <p role="status">Loading claim detail…</p>
              ) : item ? (
                <>
                  <p>
                    {item.claimNumber ?? "Claim number unavailable"} ·{" "}
                    {item.classification === "SYNTHETIC"
                      ? "Synthetic demonstration — production use prohibited"
                      : "Historical source evidence"}
                  </p>
                  <dl className="grid gap-2 text-sm sm:grid-cols-2">
                    {Object.entries({
                      Status: item.status,
                      "Failure code": item.failureCode,
                      Product: item.product?.name,
                      Component: item.component?.name,
                      Opened: item.openedAt,
                      Closed: item.closedAt,
                      Source: item.provenance.sourceName,
                      Authority: item.provenance.authorityClass,
                      Quality: item.provenance.quality,
                      Reconciliation: item.provenance.reconciliation,
                      Observed: item.provenance.observedAt,
                      Effective: item.provenance.effectiveAt,
                      Lineage: item.provenance.lineageId,
                      "Source connection": item.provenance.sourceConnectionId,
                      "Ingestion run": item.provenance.ingestionRunId,
                    }).map(([label, value]) => (
                      <div key={label} className="min-w-0">
                        <dt className="text-muted-foreground">{label}</dt>
                        <dd className="break-words">{value ?? "Unavailable"}</dd>
                      </div>
                    ))}
                  </dl>
                  <ul className="list-disc pl-5 text-sm">
                    {item.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </>
              ) : null}
            </section>
          )}
        </>
      )}
    </section>
  );
}
